'use client'

import {useEffect, useState} from 'react'

import type {SingleOrderSection} from '@/types/order'

import MachineImage, {
    type MachineVisualizationProduct,
    type MachineVisualizationSection,
} from './MachineImage'
import SynchronizedZoomImage, {type SynchronizedZoomState} from './SynchronizedZoomImage'

type SectionImageComparisonProps = {
    section: SingleOrderSection
    machineSection?: MachineVisualizationSection
    machineProduct?: MachineVisualizationProduct
    showMachine: boolean
    isMachineLoading: boolean
    machineStatusMessage?: string
}

const DEFAULT_ZOOM: SynchronizedZoomState = {
    isZoomed: false,
    x: 0.5,
    y: 0.5,
}

const SectionImageComparison = ({
    section,
    machineSection,
    machineProduct,
    showMachine,
    isMachineLoading,
    machineStatusMessage,
}: SectionImageComparisonProps) => {
    const [zoom, setZoom] = useState<SynchronizedZoomState>(DEFAULT_ZOOM)

    useEffect(() => {
        setZoom(DEFAULT_ZOOM)
    }, [section.snapshot])

    return (
        <>
            <div className="flex w-[300px] min-w-[300px] shrink-0 flex-col">
                <p className="mb-4 text-base font-medium">{section.name}</p>
                <SynchronizedZoomImage
                    src={section.snapshot}
                    alt={`${section.name} product preview`}
                    zoom={zoom}
                    onZoomChange={setZoom}
                />
            </div>

            {showMachine && (
                <div className="flex w-[300px] min-w-[300px] shrink-0 flex-col">
                    <p className="mb-4 text-base font-medium">
                        Machine Generated{' '}
                        <span className="text-sm text-[#D92D20]">{machineStatusMessage}</span>
                    </p>

                    {isMachineLoading ? (
                        <div className="flex h-[300px] w-full items-center justify-center overflow-hidden rounded-md border border-gray-200">
                            <img
                                src="/assets/images/Loading.gif"
                                alt="Loading machine-generated image"
                                className="h-[150px] w-[150px] object-cover"
                            />
                        </div>
                    ) : machineSection && machineProduct ? (
                        <MachineImage
                            section={machineSection}
                            product={machineProduct}
                            geometry={{
                                width: section.section_dimensions?.width,
                                height: section.section_dimensions?.height,
                                viewport: section.snapshot_viewport,
                            }}
                            zoom={zoom}
                            onZoomChange={setZoom}
                        />
                    ) : (
                        <div className="flex h-[300px] w-[300px] items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-lg text-center text-sm text-gray-400">
                            No machine-generated image.
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default SectionImageComparison
